import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { UserIcon, EnvelopeIcon, PhoneIcon, BuildingOfficeIcon, TagIcon } from '@heroicons/react/24/outline';

interface AdherentFormData {
  siret: string;
  contactName: string;
  contactPhone: string;
  contactEmail: string;
  description: string;
}

const AdherentEdit: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [formData, setFormData] = useState<AdherentFormData>({
    siret: '',
    contactName: '',
    contactPhone: '',
    contactEmail: '',
    description: '',
  });
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [formErrors, setFormErrors] = useState<Partial<Record<keyof AdherentFormData, string>>>({});

  useEffect(() => {
    fetchAdherentData();
  }, [id]);

  const fetchAdherentData = async () => {
    setIsLoading(true);
    setError(null);

    try {
      const response = await axios.get(
        `${import.meta.env.VITE_API_BASE_URL}/adherents/${id}`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`,
          },
        }
      );

      const adherent = response.data;
      setFormData({
        siret: adherent.siret || '',
        contactName: adherent.contactName || '',
        contactPhone: adherent.contactPhone || '',
        contactEmail: adherent.contactEmail || '',
        description: adherent.description || '',
      });
    } catch (error: any) {
      console.error('Error fetching adherent data:', error);
      setError(
        error.response?.data?.message ||
          'Une erreur est survenue lors de la récupération des données de l\'adhérent'
      );
    } finally {
      setIsLoading(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    // Clear error for this field when user starts typing
    if (formErrors[name as keyof AdherentFormData]) {
      setFormErrors((prev) => ({
        ...prev,
        [name]: undefined,
      }));
    }
  };

  const validateForm = (): boolean => {
    const errors: Partial<Record<keyof AdherentFormData, string>> = {};
    let isValid = true;

    // SIRET validation (14 digits)
    if (!formData.siret) {
      errors.siret = 'Le SIRET est obligatoire';
      isValid = false;
    } else if (!/^\d{14}$/.test(formData.siret)) {
      errors.siret = 'Le SIRET doit contenir 14 chiffres';
      isValid = false;
    }

    // Email validation
    if (formData.contactEmail && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.contactEmail)) {
      errors.contactEmail = 'Format d\'email invalide';
      isValid = false;
    }

    // Phone validation (simple format check)
    if (formData.contactPhone && !/^[+\d\s()-]{6,20}$/.test(formData.contactPhone)) {
      errors.contactPhone = 'Format de téléphone invalide';
      isValid = false;
    }

    setFormErrors(errors);
    return isValid;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsSaving(true);
    setError(null);

    try {
      await axios.put(
        `${import.meta.env.VITE_API_BASE_URL}/adherents/${id}`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`,
            'Content-Type': 'application/json',
          },
        }
      );

      navigate(`/adherents/${id}`, {
        state: {
          notification: {
            type: 'success',
            message: 'Adhérent mis à jour avec succès',
          },
        },
      });
    } catch (error: any) {
      console.error('Error updating adherent:', error);
      setError(
        error.response?.data?.message ||
          'Une erreur est survenue lors de la mise à jour de l\'adhérent'
      );
      setIsSaving(false);
    }
  };

  if (isLoading) {
    return (
      <div className="p-6">
        <div className="text-center py-8">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Chargement des données de l'adhérent...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      <div className="-mt-4">
        <Link
          to={`/adherents/${id}`}
          className="text-blue-600 hover:text-blue-800 flex items-center"
        >
          <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
          </svg>
          Retour aux détails de l'adhérent
        </Link>
      </div>
      
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">Modifier l'adhérent</h1>
          <p className="mt-1 text-sm text-gray-500">
            Mettez à jour les informations de contact de cet adhérent.
          </p>
        </div>
      </div>

      {error && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative">
          <strong className="font-bold">Erreur: </strong>
          <span className="block sm:inline">{error}</span>
        </div>
      )}

      <div className="bg-white shadow rounded-lg overflow-hidden border border-gray-200">
        <div className="px-4 py-5 sm:px-6 bg-blue-50 border-b border-gray-200">
          <div className="flex items-center">
            <div className="bg-blue-100 rounded-full p-2 mr-3">
              <UserIcon className="h-6 w-6 text-blue-600" />
            </div>
            <h3 className="text-lg font-medium leading-6 text-gray-900">
              Carte de contact
            </h3>
          </div>
          <p className="mt-1 max-w-2xl text-sm text-gray-500">
            Informations saisies manuellement
          </p>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          <div className="grid grid-cols-1 gap-y-6 md:grid-cols-2 gap-x-6">
            <div className="col-span-1">
              <div className="flex items-start">
                <div className="mt-1 bg-gray-100 rounded-full p-2 mr-4 flex-shrink-0">
                  <TagIcon className="h-5 w-5 text-gray-600" />
                </div>
                <div className="flex-1">
                  <label htmlFor="siret" className="block text-sm font-medium text-gray-700">
                    SIRET <span className="text-red-500">*</span>
                  </label>
                  <div className="mt-1">
                    <input
                      type="text"
                      name="siret"
                      id="siret"
                      value={formData.siret}
                      onChange={handleChange}
                      className={`shadow-sm focus:ring-blue-500 focus:border-blue-500 block w-full sm:text-sm border-gray-300 rounded-md ${
                        formErrors.siret ? 'border-red-300 ring-1 ring-red-300' : ''
                      }`}
                      required
                    />
                    {formErrors.siret && (
                      <p className="mt-1 text-sm text-red-600">{formErrors.siret}</p>
                    )}
                  </div>
                </div>
              </div>
            </div>

            <div className="col-span-1">
              <div className="flex items-start">
                <div className="mt-1 bg-gray-100 rounded-full p-2 mr-4 flex-shrink-0">
                  <UserIcon className="h-5 w-5 text-gray-600" />
                </div>
                <div className="flex-1">
                  <label htmlFor="contactName" className="block text-sm font-medium text-gray-700">
                    Nom du contact
                  </label>
                  <div className="mt-1">
                    <input
                      type="text"
                      name="contactName"
                      id="contactName"
                      value={formData.contactName}
                      onChange={handleChange}
                      className="shadow-sm focus:ring-blue-500 focus:border-blue-500 block w-full sm:text-sm border-gray-300 rounded-md"
                      placeholder="Nom et prénom"
                    />
                  </div>
                </div>
              </div>
            </div>

            <div className="col-span-1">
              <div className="flex items-start">
                <div className="mt-1 bg-gray-100 rounded-full p-2 mr-4 flex-shrink-0">
                  <EnvelopeIcon className="h-5 w-5 text-gray-600" />
                </div>
                <div className="flex-1">
                  <label htmlFor="contactEmail" className="block text-sm font-medium text-gray-700">
                    Email du contact
                  </label>
                  <div className="mt-1">
                    <input
                      type="email"
                      name="contactEmail"
                      id="contactEmail"
                      value={formData.contactEmail}
                      onChange={handleChange}
                      className={`shadow-sm focus:ring-blue-500 focus:border-blue-500 block w-full sm:text-sm border-gray-300 rounded-md ${
                        formErrors.contactEmail ? 'border-red-300 ring-1 ring-red-300' : ''
                      }`}
                      placeholder="email@example.com"
                    />
                    {formErrors.contactEmail && (
                      <p className="mt-1 text-sm text-red-600">{formErrors.contactEmail}</p>
                    )}
                  </div>
                </div>
              </div>
            </div>

            <div className="col-span-1">
              <div className="flex items-start">
                <div className="mt-1 bg-gray-100 rounded-full p-2 mr-4 flex-shrink-0">
                  <PhoneIcon className="h-5 w-5 text-gray-600" />
                </div>
                <div className="flex-1">
                  <label htmlFor="contactPhone" className="block text-sm font-medium text-gray-700">
                    Téléphone du contact
                  </label>
                  <div className="mt-1">
                    <input
                      type="tel"
                      name="contactPhone"
                      id="contactPhone"
                      value={formData.contactPhone}
                      onChange={handleChange}
                      className={`shadow-sm focus:ring-blue-500 focus:border-blue-500 block w-full sm:text-sm border-gray-300 rounded-md ${
                        formErrors.contactPhone ? 'border-red-300 ring-1 ring-red-300' : ''
                      }`}
                      placeholder="+33 1 23 45 67 89"
                    />
                    {formErrors.contactPhone && (
                      <p className="mt-1 text-sm text-red-600">{formErrors.contactPhone}</p>
                    )}
                  </div>
                </div>
              </div>
            </div>

            <div className="md:col-span-2">
              <div className="flex items-start">
                <div className="mt-1 bg-gray-100 rounded-full p-2 mr-4 flex-shrink-0">
                  <BuildingOfficeIcon className="h-5 w-5 text-gray-600" />
                </div>
                <div className="flex-1">
                  <label htmlFor="description" className="block text-sm font-medium text-gray-700">
                    Description de l'organisation
                  </label>
                  <div className="mt-1">
                    <textarea
                      id="description"
                      name="description"
                      rows={4}
                      value={formData.description}
                      onChange={handleChange}
                      className="shadow-sm focus:ring-blue-500 focus:border-blue-500 block w-full sm:text-sm border-gray-300 rounded-md"
                      placeholder="Informations complémentaires sur cet adhérent..."
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="flex justify-end space-x-3 pt-6 border-t border-gray-200">
            <Link
              to={`/adherents/${id}`}
              className="px-4 py-2 border border-gray-300 bg-white text-gray-700 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors"
            >
              Annuler
            </Link>
            <button
              type="submit"
              disabled={isSaving}
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors"
            >
              {isSaving ? 'Enregistrement...' : 'Enregistrer'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AdherentEdit; 